import { useCallback, useEffect, useRef, useState } from "react";
import { getTournament, liveTournamentUrl } from "./client";
import type { Tournament } from "@dowty/tournament-ui";
import type { AuthUser } from "../context/AuthContext";

const POLL_MS = 5000;

async function readTournamentEvents(
    body: ReadableStream<Uint8Array>,
    signal: AbortSignal,
    onUpdate: () => void
) {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (!signal.aborted) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split(/\r?\n\r?\n/);
        buffer = events.pop() ?? "";
        for (const event of events) {
            if (event.split(/\r?\n/).some((line) => /^event:\s*v\s*$/.test(line))) onUpdate();
        }
    }
}

type LiveTournamentState = {
    view: Tournament | null;
    loading: boolean;
    stale: boolean;
    error: string | null;
    updatedAt: number | null;
    refresh: () => Promise<void>;
};

export function useLiveTournament(id: number | null, user: AuthUser | null): LiveTournamentState {
    const [view, setView] = useState<Tournament | null>(null);
    const [loading, setLoading] = useState(true);
    const [stale, setStale] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [updatedAt, setUpdatedAt] = useState<number | null>(null);
    const passwordRef = useRef("");
    const passwordTournamentId = useRef<number | null>(null);
    const loadRef = useRef<() => Promise<void>>(async () => undefined);
    const refresh = useCallback(() => loadRef.current(), []);

    useEffect(() => {
        let active = true;
        let streamController: AbortController | null = null;
        let poll: number | undefined;
        let retry: number | undefined;
        let retryDelay = 1000;
        if (passwordTournamentId.current !== id) {
            const params = new URLSearchParams(window.location.search);
            passwordRef.current = params.get("password") ?? "";
            params.delete("password");
            const search = params.toString();
            const query = search ? `?${search}` : "";
            window.history.replaceState(window.history.state, "", `${window.location.pathname}${query}${window.location.hash}`);
            passwordTournamentId.current = id;
        }
        const params = new URLSearchParams(window.location.search);

        setView(null);
        setStale(false);
        setError(null);
        setUpdatedAt(null);

        const load = async () => {
            if (id === null || !user) {
                setLoading(false);
                return;
            }
            setLoading(true);
            try {
                const next = await getTournament(id, user, passwordRef.current, params.get("share") ?? "");
                if (!active) return;
                setView((current) => current?.id === next.id && current.version > next.version ? current : next);
                setError(null);
                setStale(false);
                setUpdatedAt(Date.now());
            } catch (reason) {
                if (!active) return;
                setStale(true);
                setError(reason instanceof Error ? reason.message : "Could not load tournament");
            } finally {
                if (active) setLoading(false);
            }
        };
        loadRef.current = load;

        const startPolling = () => {
            poll ??= window.setInterval(() => { void load(); }, POLL_MS);
        };

        const connect = async () => {
            if (!active || id === null || !user || streamController) return;
            const controller = new AbortController();
            streamController = controller;
            const headers: Record<string, string> = { Accept: "text/event-stream" };
            if (user.token) headers.Authorization = `Bearer ${user.token}`;
            const share = params.get("share");
            const password = passwordRef.current;
            if (share) headers["X-Tournament-Share"] = share;
            if (password) headers["X-Tournament-Password"] = password;
            try {
                const response = await window.fetch(liveTournamentUrl(id), {
                    headers,
                    credentials: "include",
                    signal: controller.signal,
                });
                if (!response.ok || !response.body) throw new Error("Live tournament stream unavailable");
                retryDelay = 1000;
                if (poll !== undefined) window.clearInterval(poll);
                poll = undefined;

                await readTournamentEvents(response.body, controller.signal, () => { void load(); });
            } catch {
                if (!active || controller.signal.aborted) return;
            }
            if (streamController === controller) streamController = null;
            if (active) {
                startPolling();
                retry = window.setTimeout(() => { retry = undefined; void connect(); }, retryDelay);
                retryDelay = Math.min(retryDelay * 2, 30000);
            }
        };

        setLoading(true);
        void load();
        if (id !== null && user) void connect();
        return () => {
            active = false;
            if (loadRef.current === load) loadRef.current = async () => undefined;
            streamController?.abort();
            if (poll !== undefined) window.clearInterval(poll);
            if (retry !== undefined) window.clearTimeout(retry);
        };
    }, [id, user]);

    return { view, loading, stale, error, updatedAt, refresh };
}
